/**
 * AgentOsDemoProductDesc.jsx
 *
 * Public sandbox for the Product Description Optimizer agent.
 * Rate-limited 3/IP/24h (shared with other demos) on the backend.
 */
import { useState } from "react";
import axios from "axios";
import { API } from "../config";
import {
  Sparkles, ArrowRight, Loader2, Copy, CheckCircle, Lock, FileText,
} from "lucide-react";
import AgentOsDemoShell from "./AgentOsDemoShell";

const VOICES = [
  { id: "warm", name: "Vrúcny" },
  { id: "formal", name: "Formálny" },
  { id: "friendly", name: "Priateľský" },
  { id: "premium", name: "Premium" },
];
const LANGS = [
  { id: "sk", name: "Slovenčina" }, { id: "cs", name: "Čeština" },
  { id: "en", name: "English" }, { id: "de", name: "Deutsch" },
  { id: "pl", name: "Polski" }, { id: "hu", name: "Magyar" },
];

const EXAMPLES = [
  { product_name: "Bezdrôtové slúchadlá BoomBass X3", category: "Elektronika", keywords: "slúchadlá, bluetooth, herné" },
  { product_name: "Detský kočík AllRoad 3v1", category: "Detské potreby", keywords: "kočík, 3v1, AllRoad" },
  { product_name: "Pánska športová bunda Stormproof", category: "Oblečenie", keywords: "bunda, pánska, vodeodolná" },
];

export default function AgentOsDemoProductDesc() {
  const [form, setForm] = useState({
    product_name: "", current_description: "", category: "", keywords: "",
    brand_name: "", brand_voice: "warm", lang: "sk",
  });
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState({ loading: false, error: null });
  const [copied, setCopied] = useState(false);

  const loadExample = (i) => { setForm((f) => ({ ...f, ...EXAMPLES[i] })); setResult(null); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.product_name.trim()) return;
    setStatus({ loading: true, error: null }); setResult(null);
    try {
      const r = await axios.post(`${API}/agent-os/demo/product-desc`, form);
      setResult(r.data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setStatus({ loading: false, error: typeof d === "object" ? d : { message: d || "Skús to ešte raz." } });
      return;
    }
    setStatus({ loading: false, error: null });
  };

  const copyAll = async () => {
    if (!result?.output) return;
    const o = result.output;
    const txt = `Title: ${o.title}\n\nDescription:\n${o.description}\n\nBullets:\n${(o.bullets || []).map((b) => "• " + b).join("\n")}\n\nMeta: ${o.meta_description}`;
    try { await navigator.clipboard.writeText(txt); setCopied(true); } catch {}
    setTimeout(() => setCopied(false), 2000);
  };

  const Left = (
    <form onSubmit={submit} className="space-y-4 p-6 bg-black border border-gray-800">
      <h2 className="text-sm font-black uppercase tracking-widest text-gray-400 mb-3 flex items-center gap-2">
        <FileText className="w-4 h-4" /> Produkt
      </h2>

      <div className="flex flex-wrap gap-2">
        <span className="text-xs uppercase tracking-widest text-gray-500 self-center mr-2">Príklad:</span>
        {EXAMPLES.map((ex, i) => (
          <button
            key={i} type="button" onClick={() => loadExample(i)}
            data-testid={`agent-os-demo-pd-example-${i}`}
            className="px-3 py-1 text-xs bg-[#0a0a0a] border border-gray-800 hover:border-emerald-700/60 text-gray-300 hover:text-emerald-300"
          >{ex.product_name.slice(0, 24)}…</button>
        ))}
      </div>

      <input required type="text" placeholder="Názov produktu *"
        value={form.product_name} onChange={(e) => setForm({ ...form, product_name: e.target.value })}
        data-testid="agent-os-demo-pd-product"
        className="w-full px-3 py-2 text-sm bg-[#0a0a0a] border border-gray-800 focus:border-emerald-500 outline-none text-white placeholder-gray-600" />

      <textarea rows={3} placeholder="Aktuálny popis (voliteľné)"
        value={form.current_description} onChange={(e) => setForm({ ...form, current_description: e.target.value })}
        data-testid="agent-os-demo-pd-current"
        className="w-full px-3 py-2 text-sm bg-[#0a0a0a] border border-gray-800 focus:border-emerald-500 outline-none text-white placeholder-gray-600" />

      <div className="grid grid-cols-2 gap-3">
        <input type="text" placeholder="Kategória"
          value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
          data-testid="agent-os-demo-pd-category"
          className="px-3 py-2 text-sm bg-[#0a0a0a] border border-gray-800 focus:border-emerald-500 outline-none text-white placeholder-gray-600" />
        <input type="text" placeholder="Keywords"
          value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })}
          data-testid="agent-os-demo-pd-keywords"
          className="px-3 py-2 text-sm bg-[#0a0a0a] border border-gray-800 focus:border-emerald-500 outline-none text-white placeholder-gray-600" />
      </div>

      <hr className="border-gray-800" />
      <h3 className="text-sm font-black uppercase tracking-widest text-gray-400">Tvoj brand</h3>
      <input type="text" placeholder="Názov e-shopu"
        value={form.brand_name} onChange={(e) => setForm({ ...form, brand_name: e.target.value })}
        data-testid="agent-os-demo-pd-brand"
        className="w-full px-3 py-2 text-sm bg-[#0a0a0a] border border-gray-800 focus:border-emerald-500 outline-none text-white placeholder-gray-600" />

      <div className="grid grid-cols-2 gap-3">
        <select value={form.brand_voice} onChange={(e) => setForm({ ...form, brand_voice: e.target.value })}
          data-testid="agent-os-demo-pd-voice"
          className="px-3 py-2 text-sm bg-[#0a0a0a] border border-gray-800 focus:border-emerald-500 outline-none text-white">
          {VOICES.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
        <select value={form.lang} onChange={(e) => setForm({ ...form, lang: e.target.value })}
          data-testid="agent-os-demo-pd-lang"
          className="px-3 py-2 text-sm bg-[#0a0a0a] border border-gray-800 focus:border-emerald-500 outline-none text-white">
          {LANGS.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
      </div>

      <button type="submit" disabled={status.loading || !form.product_name.trim()}
        data-testid="agent-os-demo-pd-submit"
        className="w-full px-5 py-3 bg-emerald-400 hover:bg-emerald-300 disabled:opacity-40 disabled:cursor-not-allowed text-black font-black uppercase tracking-widest text-sm flex items-center justify-center gap-2">
        {status.loading
          ? <><Loader2 className="w-4 h-4 animate-spin" /> Generujem…</>
          : <>Optimalizuj popis <Sparkles className="w-4 h-4" /></>}
      </button>
    </form>
  );

  const Right = (
    <div className="space-y-4">
      <div className="p-6 bg-gradient-to-br from-emerald-950/30 to-black border-2 border-emerald-700/40 min-h-[300px]">
        <h2 className="text-sm font-black uppercase tracking-widest text-emerald-300 mb-3">AI Optimalizovaný popis</h2>

        {!result && !status.loading && !status.error && (
          <div className="text-center py-12 text-gray-500 text-sm" data-testid="agent-os-demo-pd-empty">
            <Sparkles className="w-8 h-8 mx-auto mb-3 text-gray-700" />
            Zadaj produkt a klikni „Optimalizuj"
          </div>
        )}
        {status.loading && (
          <div className="text-center py-12 text-emerald-300 text-sm" data-testid="agent-os-demo-pd-loading">
            <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin" /> AI píše copy…
          </div>
        )}
        {status.error && (
          <div className="p-4 bg-rose-950/30 border border-rose-700/40 text-rose-200 text-sm" data-testid="agent-os-demo-pd-error">
            <div className="font-bold mb-1">{status.error?.message || "Niečo zlyhalo"}</div>
            {status.error?.cta_url && (
              <a href={status.error.cta_url} className="inline-flex items-center gap-1 mt-2 text-emerald-300 underline text-xs font-bold">
                Registruj sa zadarmo <ArrowRight className="w-3 h-3" />
              </a>
            )}
          </div>
        )}

        {result?.output && (
          <div data-testid="agent-os-demo-pd-result" className="space-y-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold mb-1">Title (SEO)</div>
              <div className="p-3 bg-black/50 border border-emerald-900/40 text-white">{result.output.title}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold mb-1">Popis</div>
              <div className="p-3 bg-black/50 border border-emerald-900/40 text-white text-sm whitespace-pre-line">{result.output.description}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold mb-1">Bullets</div>
              <ul className="p-3 bg-black/50 border border-emerald-900/40 text-white text-sm space-y-1 list-disc list-inside">
                {(result.output.bullets || []).map((b, i) => <li key={i}>{b}</li>)}
              </ul>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold mb-1">Meta description</div>
              <div className="p-3 bg-black/50 border border-emerald-900/40 text-white text-xs italic">{result.output.meta_description}</div>
            </div>
            <div className="flex flex-wrap gap-2 text-[10px] uppercase tracking-widest">
              <span className="px-2 py-1 bg-gray-900 text-gray-400">{result.latency_ms} ms</span>
              {result.used_fallback && <span className="px-2 py-1 bg-amber-900/30 text-amber-300 font-bold">Fallback</span>}
              <span className="px-2 py-1 bg-gray-900 text-gray-400">Zostáva dnes: {result.remaining_today}</span>
            </div>
            <div className="flex gap-2">
              <button onClick={copyAll} data-testid="agent-os-demo-pd-copy"
                className="flex-1 px-4 py-2 border border-gray-700 hover:border-emerald-400 text-xs font-bold uppercase tracking-widest text-white flex items-center justify-center gap-2">
                {copied ? <><CheckCircle className="w-3 h-3 text-emerald-400" /> Skopírované</> : <><Copy className="w-3 h-3" /> Kopírovať</>}
              </button>
              <a href="/agent-os/onboard" data-testid="agent-os-demo-pd-cta"
                className="flex-1 px-4 py-2 bg-emerald-400 hover:bg-emerald-300 text-black text-xs font-black uppercase tracking-widest flex items-center justify-center gap-2">
                Aktivovať <ArrowRight className="w-3 h-3" />
              </a>
            </div>
          </div>
        )}
      </div>

      <div className="p-4 bg-black border border-gray-800 text-xs text-gray-500 flex gap-3 items-start">
        <Lock className="w-4 h-4 shrink-0 mt-0.5 text-gray-600" />
        <div>Demo má limit 3 generácie / IP / 24h. <a href="/privacy" className="text-emerald-400 underline ml-1">Privacy →</a></div>
      </div>
    </div>
  );

  return (
    <AgentOsDemoShell
      testid="agent-os-demo-product-desc-page"
      h1={<>Product Desc <span className="text-emerald-400">Optimizer</span></>}
      lead="Vlož názov produktu → AI prepíše titulok, popis, bullets a meta description optimalizované pre Google + AI Search. 3 pokusy zadarmo, žiadna registrácia."
      left={Left}
      right={Right}
    />
  );
}
